
import { Options, Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import {
  IAudienceBuilderConditionColumn,
  IAudienceBuilderConditionSetting,
  IGoal,
  IInitialCampaign,
  IPushCampaign,
  IStory,
  ITrafficSource,
} from '@/utils/types'
import cloneDeep from 'lodash/cloneDeep'
import DateSelectorV3 from '@/components/common/ui/DateSelectorV3.vue'
import { ACTION_TRAFFIC_SOURCE } from '@/store/actions'

@Options({
  components: { DateSelectorV3 },
  directives: { maska },
  emits: ['update:modelValue', 'deleteItem'],
})
export default class ConditionItem extends Vue {
  @Prop({})
  modelValue!: IAudienceBuilderConditionSetting

  @Prop({})
  index!: number

  @Prop({})
  length!: number

  @Prop({})
  isAudienceBuilder!: boolean

  conditionColumnOptions: IAudienceBuilderConditionColumn[] = []

  form: IAudienceBuilderConditionSetting = {
    _id: '',
    type: '',
    data_id: [],
    column_name: '',
    text: '',
    filter: 'include',
    date: '',
    date_type: '',
    date_filter: '',
    xdays_filter: '',
    data_type: '',
    data_filter: 'sent',
  }

  isMounted = false

  get normalizedDataId() {
    // Ensure data_id is always an array for q-select with multiple
    if (Array.isArray(this.form.data_id)) {
      return this.form.data_id
    }
    return []
  }

  set normalizedDataId(value: any[]) {
    // Update form.data_id when the computed property is set
    this.form.data_id = Array.isArray(value) ? value : []
  }

  get shortStories() {
    return this.$store.getters.shortStories
  }

  get shortInitials() {
    return this.$store.getters.shortInitials
  }

  get shortPushs() {
    return this.$store.getters.shortPushs
  }

  get goals() {
    return this.$store.getters.goals
  }

  get columns() {
    return this.$store.getters.columns
  }

  get qrCodes() {
    return this.$store.getters.qrCodes || []
  }

  get conditionTypeOptions() {
    return [
      {
        value: 'story',
        label: this.$t('label.audience.story'),
        description: this.$t('label.audience.story_description'),
      },
      {
        value: 'initial_message',
        label: this.$t('label.audience.initial_message'),
        description: this.$t('label.audience.initial_message_description'),
      },
      {
        value: 'push_message',
        label: this.$t('label.audience.push_message'),
        description: this.$t('label.audience.push_message_description'),
      },
      {
        value: 'goal',
        label: this.$t('label.audience.goal'),
        description: this.$t('label.audience.goal_description'),
      },
      {
        value: 'qr_code',
        label: this.$t('label.audience.qr_code'),
        description: this.$t('label.audience.qr_code_description'),
      },
      // {
      //   value: 'date_added',
      //   label: this.$t('label.audience.date_added'),
      //   description: '',
      // },
      {
        value: 'external_data',
        label: this.$t('label.audience.external_data'),
        description: this.$t('label.audience.external_data_description'),
      },
    ]
  }

  get dataFilterOptions() {
    if (this.form.type === 'external_data') {
      if (this.form.data_type === 'number') {
        return [
          {
            value: 'equal',
            label: this.$t('label.audience.equal'),
          },
          {
            value: 'not_equal',
            label: this.$t('label.audience.not_equal'),
          },
          {
            value: 'greater_equal',
            label: this.$t('label.audience.greater_equal'),
          },
          {
            value: 'smaller_equal',
            label: this.$t('label.audience.smaller_equal'),
          },
        ]
      }
      return [
        {
          value: 'equal',
          label: this.$t('label.audience.equal'),
        },
        {
          value: 'not_equal',
          label: this.$t('label.audience.not_equal'),
        },
        {
          value: 'include',
          label: this.$t('label.audience.include'),
        },
        {
          value: 'exclude',
          label: this.$t('label.audience.exclude'),
        },
      ]
    } else if (this.form.type === 'goal') {
      return [
        {
          value: 'reached',
          label: this.$t('label.audience.reached'),
        },
        {
          value: 'not_reached',
          label: this.$t('label.audience.not_reached'),
        },
      ]
    } else if (this.form.type === 'qr_code') {
      return [
        {
          value: 'used',
          label: this.$t('label.audience.used'),
        },
        {
          value: 'not_used',
          label: this.$t('label.audience.not_used'),
        },
      ]
    }
    return [
      {
        value: 'sent',
        label: this.$t('label.audience.sent'),
      },
      {
        value: 'not_sent',
        label: this.$t('label.audience.not_sent'),
      },
    ]
  }

  get dateTypeOptions() {
    return [
      {
        value: '',
        label: this.$t('label.audience.no_date'),
      },
      {
        value: 'absolute',
        label: this.$t('label.audience.absolute'),
      },
      {
        value: 'relative',
        label: this.$t('label.audience.relative'),
      },
      {
        value: 'xdays',
        label: this.$t('label.audience.xdays'),
      },
    ]
  }

  get dateFilterOptions() {
    return [
      {
        value: 'match',
        label: this.$t('label.audience.match'),
      },
      {
        value: 'not_match',
        label: this.$t('label.audience.not_match'),
      },
      {
        value: 'before',
        label: this.$t('label.audience.before'),
      },
      {
        value: 'after',
        label: this.$t('label.audience.after'),
      },
      {
        value: 'previous',
        label: this.$t('label.audience.previous'),
      },
      {
        value: 'onward',
        label: this.$t('label.audience.onward'),
      },
    ]
  }

  get relativeDateOptions() {
    return [
      {
        value: 'yesterday',
        label: this.$t('label.audience.yesterday'),
      },
      {
        value: 'today',
        label: this.$t('label.audience.today'),
      },
      {
        value: 'tomorrow',
        label: this.$t('label.audience.tomorrow'),
      },
      {
        value: 'last_week',
        label: this.$t('label.audience.last_week'),
      },
      {
        value: 'this_week',
        label: this.$t('label.audience.this_week'),
      },
      {
        value: 'next_week',
        label: this.$t('label.audience.next_week'),
      },
      {
        value: 'last_month',
        label: this.$t('label.audience.last_month'),
      },
      {
        value: 'this_month',
        label: this.$t('label.audience.this_month'),
      },
      {
        value: 'next_month',
        label: this.$t('label.audience.next_month'),
      },
    ]
  }

  get xDaysFilterOptions() {
    return [
      {
        value: 'backward',
        label: this.$t('label.audience.backward'),
      },
      {
        value: 'forward',
        label: this.$t('label.audience.forward'),
      },
    ]
  }

  get externalDataBooleanOptions() {
    return [
      {
        value: 1,
        label: this.$t('label.audience.true'),
      },
      {
        value: 0,
        label: this.$t('label.audience.false'),
      },
    ]
  }

  get selectedExternalDataColumnType(): string | undefined {
    if (this.form.type !== 'external_data' || !this.form.column_name) {
      return undefined
    }
    const selectedColumn = this.conditionColumnOptions.find((col) => col.value === this.form.column_name)
    if (selectedColumn?.data_type === 'number' && selectedColumn?.label?.startsWith('is_')) {
      return 'boolean'
    }
    return selectedColumn?.data_type
  }

  conditionTypeLabel(type: string): string {
    return this.conditionTypeOptions.find((option) => option.value === type)?.label
  }

  conditionTypeDescription(type: string): string {
    return this.conditionTypeOptions.find((option) => option.value === type)?.description
  }

  deleteItem(index: number) {
    this.$emit('deleteItem', index)
  }

  isSameForm(a: any, b: any) {
    return JSON.stringify(a) === JSON.stringify(b)
  }

  @Watch('modelValue', { deep: true, immediate: true })
  onModelValueChange(newVal: IAudienceBuilderConditionSetting) {
    // Don't overwrite form if we're currently updating text (to prevent losing user input)
    if (this.isTextInputUpdating || this.debounceTimer) {
      return
    }
    if (this.isSameForm(newVal, this.modelValue)) {
      return
    }
    if (newVal) {
      this.form = {
        ...newVal,
        // Ensure data_id is always an array for q-select with multiple
        data_id: Array.isArray(newVal.data_id) ? newVal.data_id : [],
      }
    }
  }

  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private isTextInputUpdating = false

  onTextInput(value: string | number, type = 'text') {
    const stringValue = String(value)

    // Set flag to prevent watch from interfering
    this.isTextInputUpdating = true

    // Update form.text immediately for UI responsiveness
    if (type === 'text') {
      this.form.text = stringValue
    } else if (type === 'date') {
      this.form.date = stringValue
    }

    // Use nextTick to ensure form is updated before clearing flag
    this.$nextTick(() => {
      this.isTextInputUpdating = false
    })

    // Clear any existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Set new timer - will emit after 500ms of no typing
    // Capture the value in closure to ensure we use the latest
    this.debounceTimer = setTimeout(() => {
      const currentForm = { ...this.form }
      this.$emit('update:modelValue', currentForm)
      this.debounceTimer = null
    }, 500)
  }

  @Watch('form', { deep: true })
  onFormChange(newVal: IAudienceBuilderConditionSetting, oldVal: IAudienceBuilderConditionSetting) {
    // Skip if we're updating text (handled by onTextInput with debounce)
    if (this.isTextInputUpdating) {
      return
    }

    if (this.isSameForm(newVal, this.modelValue)) {
      return
    }

    // Skip if only text changed (handled by onTextInput with debounce)
    if (
      oldVal &&
      newVal.text !== oldVal.text &&
      (newVal.type === 'external_data' ||
        newVal.type === 'story' ||
        newVal.type === 'initial_message' ||
        newVal.type === 'push_message' ||
        newVal.type === 'goal')
    ) {
      // Text changes are handled by onTextInput with debounce
      return
    }

    console.log('onFormChange', newVal)
    // Cancel any pending debounced text input calls
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    // Emit immediately for other changes (type selection, column selection, filter selection)
    // Ensure data_id is always an array when emitting
    this.$emit('update:modelValue', {
      ...newVal,
      data_id: Array.isArray(newVal.data_id) ? newVal.data_id : [],
    })
  }

  beforeUnmount() {
    // Clean up timer on component destroy
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  @Watch('form.type')
  async onTypeChange(type: string) {
    console.log('onTypeChange', type)
    if (!this.isMounted) return
    await this.loadDropdownOptions(type)
    this.form.type = type
    this.form.data_id = []
    this.form.column_name = ''
    this.form.text = ''
    this.form.filter = 'include'
    this.form.date = ''
    this.form.date_type = ''
    this.form.date_filter = ''

    if (type === 'external_data') {
      this.form.data_filter = 'equal'
    } else if (type === 'goal') {
      this.form.data_filter = 'reached'
    } else if (type === 'qr_code') {
      this.form.data_filter = 'used'
    } else {
      this.form.data_filter = 'sent'
    }
  }

  @Watch('form.date_type')
  async onDateTypeChange(type: string) {
    if (!this.isMounted) return

    this.form.date_type = type
    this.form.text = ''
    this.form.date = ''
    this.form.data_filter = 'sent'
    this.form.date_filter = 'match'
    this.form.xdays_filter = ''

    if (type === 'xdays') {
      this.form.xdays_filter = 'backward'
    }
    if (this.form.type === 'external_data') {
      this.form.data_filter = 'equal'
    } else if (this.form.type === 'goal') {
      this.form.data_filter = 'reached'
    } else if (this.form.type === 'qr_code') {
      this.form.data_filter = 'used'
    } else {
      this.form.data_filter = 'sent'
    }
  }

  @Watch('form.data_type')
  async onDataTypeChange(type: string) {
    if (!this.isMounted) return

    this.form.data_type = type
    this.form.text = ''
    this.form.date = ''
    this.form.date_filter = ''
    this.form.data_filter = 'sent'
    this.form.xdays_filter = ''

    if (this.form.data_type === 'boolean') {
      this.form.text = 1
    }
    if (this.form.type === 'external_data') {
      this.form.data_filter = 'equal'
    } else if (this.form.type === 'goal') {
      this.form.data_filter = 'reached'
    } else if (this.form.type === 'qr_code') {
      this.form.data_filter = 'used'
    } else {
      this.form.data_filter = 'sent'
    }
  }

  async loadDropdownOptions(type: string) {
    this.conditionColumnOptions = []
    if (type === 'story') {
      this.conditionColumnOptions.push(
        ...this.shortStories.map((story: IStory) => ({
          value: story._id,
          label: story.story_name,
          type: type,
        }))
      )
    } else if (type === 'initial_message') {
      this.conditionColumnOptions.push(
        ...this.shortInitials.map((item: IInitialCampaign) => ({
          value: item._id,
          label: item.title,
          type: type,
        }))
      )
    } else if (type === 'push_message') {
      this.conditionColumnOptions.push(
        ...this.shortPushs.map((item: IPushCampaign) => ({
          value: item._id,
          label: item.title,
          type: type,
        }))
      )
    } else if (type === 'goal') {
      this.conditionColumnOptions.push(
        ...this.goals.map((item: IGoal) => ({
          value: item._id,
          label: item.title,
          type: type,
        }))
      )
    } else if (type === 'qr_code') {
      // Load QR codes from traffic sources
      const appId = this.$route?.params?.app_id as string
      if (appId) {
        await this.$store.dispatch(ACTION_TRAFFIC_SOURCE.LOAD_QR_CODES, appId)
      }
      this.conditionColumnOptions.push(
        ...this.qrCodes.map((item: ITrafficSource) => ({
          value: item._id,
          label: item.source_name,
          type: type,
        }))
      )
    } else if (type === 'external_data') {
      this.conditionColumnOptions.push(
        ...(Array.isArray(this.columns)
          ? this.columns.map((column: any) => {
              const isBoolean =
                column.column_type === 'number' &&
                typeof column.column_name === 'string' &&
                column.column_name.startsWith('is_')

              return {
                value: column.column_name,
                label: column.column_name,
                data_type: isBoolean ? 'boolean' : column.column_type,
                type: type,
              }
            })
          : [])
      )
    }
  }

  onColumnChange(value: string) {
    const option = this.conditionColumnOptions.find((opt: any) => opt.value === value)

    this.form.column_name = value
    this.form.data_type = option?.data_type
  }

  created() {
    this.form = { ...this.form, ...cloneDeep(this.modelValue) }
    if (this.form.type && this.form.type !== '') {
      this.loadDropdownOptions(this.form.type)
    }
    this.isMounted = true
  }
}
